import dayjs, { type Dayjs } from "@punchup/utils/dayjs";
import type {
  ShowReminderCandidate,
  ShowReminderDecision,
  ShowReminderDisableReason,
  ShowReminderLeadUnit,
} from "../../models/show-reminders";

const HOURS_PER_LEAD_UNIT: Record<ShowReminderLeadUnit, number> = {
  hours: 1,
  days: 24,
};

const LEAD_UNITS = Object.keys(HOURS_PER_LEAD_UNIT);

function isLeadUnit(unit: string | null): unit is ShowReminderLeadUnit {
  return unit !== null && LEAD_UNITS.includes(unit);
}

/** A reminder's lead in hours, or null for a missing, non-positive, non-finite or unknown-unit lead. */
function showReminderLeadHours(
  value: number | null,
  unit: string | null
): number | null {
  if (value === null || !Number.isFinite(value) || value <= 0) return null;
  if (!isLeadUnit(unit)) return null;
  return value * HOURS_PER_LEAD_UNIT[unit];
}

/** Why the cron switches a due reminder off for good: the response moved to another show, or the comedian left the lineup. */
export function showReminderDisableReason({
  show,
  response,
}: Pick<
  ShowReminderCandidate,
  "show" | "response"
>): ShowReminderDisableReason | null {
  if (response.show_id !== show.id) return "moved-to-another-show";
  const comedianLeftLineup =
    response.comedian_id !== null &&
    !show.lineupComedianIds.includes(response.comedian_id);
  return comedianLeftLineup ? "removed-from-lineup" : null;
}

/** What the reminders cron does with one candidate at `now`, reading the show time as wall-clock time in `venueTimezone`. */
export function decideShowReminder(
  candidate: ShowReminderCandidate,
  { now, venueTimezone }: { now: Dayjs; venueTimezone: string }
): ShowReminderDecision {
  const { show } = candidate;

  if (!show.datetime) return { action: "skip", reason: "no-show-time" };

  const showStart = dayjs.tz(show.datetime, venueTimezone);
  if (!showStart.isAfter(now)) {
    return { action: "skip", reason: "show-started" };
  }

  const leadHours = showReminderLeadHours(
    candidate.leadValue,
    candidate.leadUnit
  );
  if (leadHours === null) return { action: "skip", reason: "invalid-lead" };

  const dueAt = showStart.subtract(leadHours, "hour");
  if (now.isBefore(dueAt)) {
    return { action: "wait", dueAt: dueAt.toISOString() };
  }

  const disableReason = showReminderDisableReason(candidate);
  if (disableReason) return { action: "disable", reason: disableReason };

  return {
    action: "send",
    dueAt: dueAt.toISOString(),
    startsAt: showStart.toISOString(),
  };
}
